import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import classes from "./bookingSection.module.css";
import { fetchBookingsByFilter } from "../../../../redux/slices/bookingSlice";
import RazorPayGateway from "../../../../components/RazorPayGateway";
import RatingComponent from "./rating";
import HowItWorksUser from "../../../../components/HowItWorksUser";

const BookingComponent = () => {
  const [activeTab, setActiveTab] = useState("upcoming");
  const [orderId, setOrderId] = useState("");
  const [razorPayOpen, setRazorPayOpen] = useState(false);
  const [askRating, setAskRating] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const dispatch = useDispatch();
  const { bookings } = useSelector((state) => state.booking) || [
    { title: "test" },
  ];

  // Tab to API status mapping
  const tabToStatusMap = {
    upcoming: "confirmed",
    awaiting: "awaiting_full_payment",
    completed: "completed",
    pending: "pending_approval",
  };

  // Fetch bookings when tab changes
  useEffect(() => {
    dispatch(
      fetchBookingsByFilter({
        type: "user", // to be taken from token
        status: tabToStatusMap[activeTab],
      })
    );
  }, [activeTab, dispatch]);

  // Format date in a more readable way
  const formatDate = (dateString) => {
    const options = { weekday: "short", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  // Check if booking is within 3 days
  const isWithinThreeDays = (dateString) => {
    const bookingDate = new Date(dateString);
    const today = new Date();
    const timeDiff = bookingDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff <= 3;
  };

  // Handle reschedule booking
  const handleReschedule = (bookingId) => {
    // Implement reschedule logic here
  };

  // Handle cancel booking
  const handleCancel = (bookingId) => {
    console.log(`Cancelling booking ${bookingId}`);
  };

  // Handle payment
  const handlePayment = (booking) => {
    const latestTransaction = booking?.transactions?.filter(
      (item) => item.status === "initiated"
    );
    if (latestTransaction[0]) {
      setOrderId(latestTransaction[0]?.razorpay_order_id);
      setRazorPayOpen(true);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div className={classes.bookingContainer}>
      <div className={classes.headerSection}>
        <motion.h2
          className={classes.title}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          My Bookings
        </motion.h2>

        <motion.button
          className={classes.howItWorksButton}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowWorkflowModal(true)}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          How Bookings Work
        </motion.button>
      </div>

      {activeTab === "awaiting" && (
        <motion.div
          className={classes.paymentAlert}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={classes.alertIcon}>⚠️</div>
          <p>Complete your payment within 3 days to confirm your booking</p>
          <button
            className={classes.learnMoreBtn}
            onClick={() => setShowWorkflowModal(true)}
          >
            Learn More
          </button>
        </motion.div>
      )}

      <div className={classes.tabContainer}>
        {["upcoming", "awaiting", "pending", "completed"].map((tab) => (
          <motion.div
            key={tab}
            className={`${classes.tab} ${
              activeTab === tab ? classes.activeTab : ""
            }`}
            onClick={() => {
              setActiveTab(tab);
              setAskRating(false);
            }}
            whileHover={{ y: -2 }}
            whileTap={{ y: 1 }}
          >
            <span>
              {tab === "upcoming"
                ? "Upcoming"
                : tab === "awaiting"
                ? "Awaiting Payment"
                : tab === "pending"
                ? "Pending Approval"
                : "Completed"}
            </span>
            {tab === "pending" && (
              <div className={classes.pendingTooltip}>
                Artist responds in 24h
              </div>
            )}
            {activeTab === tab && (
              <motion.div
                className={classes.activeIndicator}
                layoutId="activeIndicator"
                initial={false}
              />
            )}
          </motion.div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          className={classes.bookingsWrapper}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={classes.bookingList}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {bookings && bookings.length > 0 ? (
              <>
                {bookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    className={classes.bookingCard}
                    variants={itemVariants}
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 8px 20px rgba(124, 8, 171, 0.15)",
                    }}
                    onMouseOver={() => {
                      // valid check here
                      if (activeTab === "completed") {
                        setAskRating(true);
                      }
                    }}
                  >
                    <div className={classes.bookingHeader}>
                      <div className={classes.bookingImageContainer}>
                        <img
                          src={booking.image}
                          alt=""
                          className={classes.bookingImage}
                        />
                      </div>
                      <div className={classes.bookingMainInfo}>
                        <div>
                          <div>
                            <h3>{booking.title}</h3>
                            <p className={classes.artistName}>
                              with {booking.artistFullName}
                            </p>
                          </div>
                          <div>
                            <p className={classes.artistName}>
                              {booking?.address}, {booking?.pincode}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className={classes.priceTag}>
                        ₹ {booking?.amount?.toFixed(2)}
                      </div>
                    </div>

                    <div className={classes.bookingDetails}>
                      <div className={classes.dateTime}>
                        <span className={classes.date}>
                          {formatDate(booking.date)}
                        </span>
                        <span className={classes.time}>{booking.time}</span>
                      </div>

                      <div className={classes.actions}>
                        {activeTab === "upcoming" && (
                          <>
                            <motion.button
                              className={`${classes.actionBtn} ${classes.viewBtn}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              View
                            </motion.button>
                            <motion.button
                              className={`${classes.actionBtn} ${classes.cancelBtn}`}
                              whileHover={{
                                scale: isWithinThreeDays(booking.date)
                                  ? 1
                                  : 1.05,
                              }}
                              whileTap={{
                                scale: isWithinThreeDays(booking.date)
                                  ? 1
                                  : 0.95,
                              }}
                              disabled={isWithinThreeDays(booking.date)}
                              onClick={() => handleCancel(booking.id)}
                              title={
                                isWithinThreeDays(booking.date)
                                  ? "Cannot cancel within 3 days of event"
                                  : ""
                              }
                            >
                              Cancel
                            </motion.button>
                          </>
                        )}

                        {activeTab === "awaiting" && (
                          <>
                            <motion.button
                              className={`${classes.actionBtn} ${classes.viewBtn}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              View
                            </motion.button>
                            <motion.button
                              className={`${classes.actionBtn} ${classes.payBtn}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handlePayment(booking)}
                            >
                              Pay Now
                            </motion.button>
                            <motion.button
                              className={`${classes.actionBtn} ${classes.cancelBtn}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleCancel(booking.id)}
                            >
                              Cancel
                            </motion.button>
                            {razorPayOpen && (
                              <RazorPayGateway orderId={orderId} />
                            )}
                          </>
                        )}

                        {activeTab === "pending" && (
                          <motion.button
                            className={`${classes.actionBtn} ${classes.viewBtn}`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            View Details
                          </motion.button>
                        )}

                        {activeTab === "completed" && (
                          <motion.button
                            className={`${classes.actionBtn} ${classes.viewBtn}`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            View Details
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {askRating && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <RatingComponent setAskRating={setAskRating} />
                  </div>
                )}
              </>
            ) : (
              <motion.div
                className={classes.emptyState}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className={classes.emptyStateIcon}>{"❗️"}</div>
                <p>
                  No{" "}
                  {activeTab === "awaiting"
                    ? "awaiting payment"
                    : activeTab === "pending"
                    ? "pending approval"
                    : activeTab}{" "}
                  bookings found
                </p>
                <button
                  className={classes.learnHowBtn}
                  onClick={() => setShowWorkflowModal(true)}
                >
                  Learn how bookings work
                </button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Booking Workflow Modal */}
      <HowItWorksUser
        isOpen={showWorkflowModal}
        onClose={() => setShowWorkflowModal(false)}
      />
    </div>
  );
};

export default BookingComponent;
